import { ReactNode, useState, useEffect, useRef, useCallback, Children } from "react";
import { twMerge } from "tailwind-merge";
import useContainerWidthBreakpoint from "@core/hooks/useContainerWidthBreakpoint";
import computeVideoGridLayout from "../../utils/computeVideoGridLayout";
import { PaginationDots } from "@themes/pollinator/ui/PaginationDots";

type Props = {
  size: number;
  children: ReactNode;
  className?: string;
  isContentShare?: boolean;
};

const GAP_PX = 16;
// Inset reserved around the grid so tiles never saturate the container edges —
// keeps the speaking ring from clipping and gives every layout a visible
// visual frame against the logo / controls bar.
const FRAME_PX = 32;

export function VideoMeetingGrid({
  size,
  children,
  className,
  isContentShare,
  ...rest
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);

  const { containerWidth, containerHeight, isXs } =
    useContainerWidthBreakpoint(ref);

  const childrenArray = Children.toArray(children);
  const gridSize = size;
  const mobileColumnSize = 2;
  const totalGroups = Math.ceil(gridSize / mobileColumnSize);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || isXs) return;

    const handleScroll = () => {
      const scrollLeft = scrollContainer.scrollLeft;
      const containerWidth = scrollContainer.clientWidth;
      const currentIndex = Math.round(scrollLeft / containerWidth);
      setCurrentGroupIndex(Math.min(currentIndex, totalGroups - 1));
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [isXs, totalGroups]);

  // Keyboard navigation: Left/Right arrow keys to navigate between tile pages
  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (totalGroups <= 1) return;
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return;

      let nextIndex = currentGroupIndex;
      if (e.key === "ArrowRight") {
        nextIndex = Math.min(currentGroupIndex + 1, totalGroups - 1);
      } else if (e.key === "ArrowLeft") {
        nextIndex = Math.max(currentGroupIndex - 1, 0);
      } else {
        return;
      }

      e.preventDefault();
      const targetScrollLeft = nextIndex * scrollContainer.clientWidth;
      scrollContainer.scrollTo({ left: targetScrollLeft, behavior: "smooth" });
      setCurrentGroupIndex(nextIndex);
    },
    [currentGroupIndex, totalGroups]
  );

  const layout = computeVideoGridLayout({
    gridSize,
    containerWidth: Math.max(0, containerWidth - FRAME_PX * 2),
    containerHeight: Math.max(0, containerHeight - FRAME_PX * 2),
    gapPx: GAP_PX,
  });

  // Split children into rows of `cols`. On full rows, justify-center is a
  // no-op (tiles fill the row width exactly). On a partial last row, the
  // remaining tiles are centered with equal slack on both sides.
  const rowGroups: ReactNode[][] = [];
  if (layout.cols > 0) {
    for (let i = 0; i < childrenArray.length; i += layout.cols) {
      rowGroups.push(childrenArray.slice(i, i + layout.cols));
    }
  }

  return (
    <div
      ref={ref}
      className={twMerge(
        "flex flex-col w-full h-full max-w-screen-4xl overflow-hidden",
        !isXs && "relative",
        isContentShare && "h-auto",
        className
      )}
      onKeyDown={handleGridKeyDown}
      tabIndex={totalGroups > 1 && !isXs ? 0 : undefined}
      role={totalGroups > 1 && !isXs ? "region" : undefined}
      aria-label={totalGroups > 1 && !isXs ? `Video grid, page ${currentGroupIndex + 1} of ${totalGroups}` : undefined}
      {...rest}
    >
      {isContentShare ? (
        <div className="flex flex-row flex-nowrap basis-36 sm:justify-start mb-4 h-auto overflow-x-auto w-full flex-1 min-h-0">
          {childrenArray.map((child, index) => (
            <div
              key={index}
              className="relative aspect-video p-2 bg-transparent basis-auto pr-2 h-full"
            >
              {child}
            </div>
          ))}
        </div>
      ) : !isXs ? (
        <div className="flex flex-row flex-wrap justify-start items-start content-start w-full flex-1 min-h-0">
          <div
            ref={scrollContainerRef}
            className="flex flex-row w-full h-full overflow-x-auto gap-4 pb-2 scroll-smooth snap-x snap-mandatory"
          >
            {Array.from({ length: totalGroups }).map((_, groupIndex) => (
              <div
                key={`group-${groupIndex}`}
                className="flex flex-col flex-shrink-0 h-full w-full min-w-full snap-start"
              >
                {childrenArray
                  .slice(
                    groupIndex * mobileColumnSize,
                    (groupIndex + 1) * mobileColumnSize
                  )
                  .map((child, itemIndex) => {
                    const index =
                      groupIndex * mobileColumnSize + itemIndex;
                    return (
                      <div
                        key={index}
                        className="relative aspect-video p-2 bg-transparent w-full"
                      >
                        {child}
                      </div>
                    );
                  })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="flex w-full h-full flex-1 min-h-0 items-center justify-center"
          style={{ padding: `${FRAME_PX}px` }}
        >
          {layout.tileWidthPx > 0 && layout.tileHeightPx > 0 ? (
            <div
              className="flex flex-col"
              style={{ gap: `${GAP_PX}px` }}
            >
              {rowGroups.map((row, rowIndex) => (
                <div
                  key={rowIndex}
                  className="flex flex-row justify-center"
                  style={{ gap: `${GAP_PX}px` }}
                >
                  {row.map((child, colIndex) => (
                    <div
                      key={colIndex}
                      className="relative aspect-video flex-shrink-0"
                      style={{
                        width: `${layout.tileWidthPx}px`,
                        height: `${layout.tileHeightPx}px`,
                      }}
                    >
                      {child}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}
      {!isXs && totalGroups > 1 && !isContentShare && (
        <PaginationDots
          totalPages={totalGroups}
          currentPageIndex={currentGroupIndex}
          scrollContainerRef={scrollContainerRef}
        />
      )}
    </div>
  );
}
